import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useCourses, categories } from '../hooks/useCourses';
import { getCourseColorOption } from '../lib/courseColors';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  Clock, 
  BookOpen, 
  Wrench, 
  ArrowLeft, 
  Calendar,
  CheckCircle,
  Users,
  FileText,
  Loader2,
  PlayCircle,
  Video,
  Info
} from 'lucide-react';

export default function CourseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { isAuthenticated } = useAuth();
  const { courses, loading, getCourseById } = useCourses();
  const course = getCourseById(id);
  const [showDetailedContent, setShowDetailedContent] = useState(false);

  const isOnDemand = course?.courseType === 'on-demand' || course?.courseType === 'hybrid';

  const handleStartWatching = () => {
    if (!isAuthenticated) {
      navigate('/register');
      return;
    }
    navigate(`/watch/${course.id}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-[#00A651]" />
          <p className="text-gray-500 mt-4">
            {t('common.loading')}
          </p>
        </div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            {t('courseDetail.courseNotFound')}
          </h1>
          <Link to="/courses">
            <Button className="bg-[#00A651] hover:bg-[#008a43]">
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t('courseDetail.backToCourses')}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const formatPrice = (price) => {
    return new Intl.NumberFormat('ja-JP').format(price);
  };

  const category = categories.find(c => c.id === course.category);
  const colorOpt = getCourseColorOption(course.color);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="course-detail-page">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link 
            to="/courses" 
            className="inline-flex items-center text-gray-600 hover:text-[#00A651] transition-colors"
            data-testid="back-to-courses"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('courseDetail.backToCourses')}
          </Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Image */}
            <div className={`relative rounded-lg overflow-hidden mb-6 ${colorOpt ? `border-t-4 ${colorOpt.border}` : ''}`}>
              <img 
                src={course.image} 
                alt={course.name[language] || course.name.ja}
                className="w-full h-64 md:h-96 object-cover"
              />
              <div className="absolute top-4 left-4 flex gap-2 flex-wrap">
                <Badge 
                  className={`${
                    course.type === 'skill' 
                      ? 'bg-[#00A651] text-white' 
                      : 'bg-[#FACC15] text-gray-900'
                  } font-semibold text-sm px-3 py-1`}
                >
                  {course.type === 'skill' ? t('courses.skillTraining') : t('courses.specialEducation')}
                </Badge>
                {category && (
                  <Badge variant="secondary" className="font-semibold text-sm px-3 py-1">
                    {category.name[language] || category.name.ja}
                  </Badge>
                )}
                {isOnDemand && (
                  <Badge className="bg-orange-500 text-white font-semibold text-sm px-3 py-1">
                    <Video className="h-3 w-3 mr-1" />
                    {course.courseType === 'hybrid' 
                      ? t('coursesPage.hybrid')
                      : t('coursesPage.onDemand')}
                  </Badge>
                )}
              </div>
            </div>

            {/* Title & Description */}
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 flex items-start gap-3">
              {colorOpt && <span className={`mt-2 w-3 h-3 rounded-full shrink-0 ${colorOpt.bg}`} />}
              <span>{course.name[language] || course.name.ja}</span>
            </h1>
            <p className="text-gray-600 leading-relaxed mb-8">
              {course.description[language] || course.description.ja}
            </p>

            {/* Course Details */}
            <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
              <h2 className="font-bold text-lg text-gray-900 mb-4">
                {t('courseDetail.courseContent')}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="flex items-start gap-3">
                  <div className="bg-[#e6f6ec] p-2 rounded-lg">
                    <Clock className="h-5 w-5 text-[#00A651]" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{t('courses.duration')}</p>
                    <p className="font-bold text-gray-900">{course.days} {t('courses.days')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="bg-[#e6f6ec] p-2 rounded-lg">
                    <BookOpen className="h-5 w-5 text-[#00A651]" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{t('courses.theory')}</p>
                    <p className="font-bold text-gray-900">{course.theoryHours} {t('courses.hours')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="bg-[#e6f6ec] p-2 rounded-lg">
                    <Wrench className="h-5 w-5 text-[#00A651]" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{t('courses.practical')}</p>
                    <p className="font-bold text-gray-900">{course.practicalHours} {t('courses.hours')}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* What You'll Learn */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="font-bold text-lg text-gray-900 mb-4">
                {t('courseDetail.whatYouLearn')}
              </h2>
              <ul className="space-y-3">
                {[
                  t('courseDetail.learn1'),
                  t('courseDetail.learn2'),
                  t('courseDetail.learn3'),
                  t('courseDetail.learn4'),
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-[#00A651] mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg border border-gray-200 p-6 sticky top-24">
              {/* Price */}
              <div className="mb-6">
                <p className="text-sm text-gray-500 mb-1">{t('courses.price')}</p>
                <p className="text-3xl font-bold text-[#00A651] font-[Barlow_Condensed]">
                  ¥{formatPrice(course.price)}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {t('courseDetail.taxIncluded')}
                </p>
              </div>

              {/* Quick Info */}
              <div className="space-y-3 mb-6 pb-6 border-b border-gray-200">
                <div className="flex items-center gap-3 text-sm">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-700">
                    {t('courseDetail.monthlySessions')}
                  </span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <Users className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-700">
                    {t('courseDetail.smallClass')}
                  </span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <FileText className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-700">
                    {t('courseDetail.certificateIssued')}
                  </span>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="space-y-3">
                {isOnDemand && (
                  <Button 
                    className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-6"
                    onClick={handleStartWatching}
                    data-testid="start-watching"
                  >
                    <PlayCircle className="h-5 w-5 mr-2" />
                    {t('courseDetail.startWatching')}
                  </Button>
                )}
                <Link to={`/apply?course=${course.id}`} className="block">
                  <Button 
                    className={`w-full ${isOnDemand ? 'bg-gray-600 hover:bg-gray-700' : 'bg-[#00A651] hover:bg-[#008a43]'} text-white font-bold py-6`}
                    data-testid="apply-for-course"
                  >
                    {isOnDemand 
                      ? t('courseDetail.applyInPerson')
                      : t('courses.applyNow')}
                  </Button>
                </Link>
                <Link to="/contact" className="block">
                  <Button 
                    variant="outline"
                    className="w-full border-[#00A651] text-[#00A651] hover:bg-[#00A651] hover:text-white font-bold"
                    data-testid="contact-about-course"
                  >
                    {t('nav.contact')}
                  </Button>
                </Link>
                <Button
                  variant="outline"
                  className="w-full border-gray-300 text-gray-700 hover:bg-gray-100 font-bold"
                  data-testid="detailed-content-btn"
                  onClick={() => setShowDetailedContent(true)}
                >
                  <Info className="h-4 w-4 mr-2" />
                  {t('courseDetail.detailedContent')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Content Dialog */}
      <Dialog open={showDetailedContent} onOpenChange={setShowDetailedContent}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">
              {t('courseDetail.detailedContent')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <h3 className="font-bold text-lg text-gray-900">
              {course?.name[language] || course?.name.ja}
            </h3>
            {course?.detailedContent?.[language] || course?.detailedContent?.ja ? (
              <div 
                className="prose prose-sm max-w-none text-gray-700"
                dangerouslySetInnerHTML={{ __html: course.detailedContent[language] || course.detailedContent.ja }}
              />
            ) : (
              <div className="space-y-4">
                <p className="text-gray-700">{course?.description[language] || course?.description.ja}</p>
                <div className="border-t pt-4">
                  <h4 className="font-semibold text-gray-900 mb-2">{t('courseDetail.courseContent')}</h4>
                  <div className="space-y-2 text-sm text-gray-700">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-[#00A651]" />
                      <span>{t('courseDetail.duration')}: {course?.days} {t('courses.days')}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <BookOpen className="h-4 w-4 text-[#00A651]" />
                      <span>{t('courses.theory')}: {course?.theoryHours} {t('courses.hours')}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Wrench className="h-4 w-4 text-[#00A651]" />
                      <span>{t('courses.practical')}: {course?.practicalHours} {t('courses.hours')}</span>
                    </div>
                  </div>
                </div>
                <div className="border-t pt-4">
                  <h4 className="font-semibold text-gray-900 mb-2">{t('courseDetail.whatYouLearn')}</h4>
                  <ul className="space-y-2">
                    {[t('courseDetail.learn1'), t('courseDetail.learn2'), t('courseDetail.learn3'), t('courseDetail.learn4')].map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                        <CheckCircle className="h-4 w-4 text-[#00A651] mt-0.5 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
